/** Optimistic cache write for DataService entities: show the change at once, persist, roll back on failure. */

/** The slice of DataService an optimistic write touches. Mixins pass `this`, cast: their base is `any`, so TS cannot see these members structurally. */
export interface OptimisticCache {
  db: Record<string, Record<string, unknown> | undefined>;
  saveToLocalStorage(): void;
  notifyListeners(): void;
}

/**
 * Puts `next` in the cache (memory and the persisted snapshot) and tells
 * listeners before `persist` runs, so the UI moves without waiting for the
 * ~1.4 s edge round trip. If `persist` rejects, the previous row comes back
 * and the error is rethrown for the caller to report.
 *
 * Rollback only fires while the cache still holds this write's object. A later
 * write to the same row, or a server refresh that replaced it, is newer than
 * anything we could restore, so it stands; that keeps memory, the snapshot and
 * the server from drifting apart after overlapping writes.
 */
export async function optimisticEntityWrite<T>(
  cache: OptimisticCache,
  collection: string,
  id: string,
  next: object,
  persist: () => Promise<T>,
): Promise<T> {
  const rows = cache.db[collection];
  if (!rows) throw new Error(`Unknown collection: ${collection}`);
  const had = Object.prototype.hasOwnProperty.call(rows, id);
  const previous = rows[id];
  rows[id] = next;
  cache.saveToLocalStorage();
  cache.notifyListeners();
  try {
    return await persist();
  } catch (error) {
    const current = cache.db[collection];
    if (current && current[id] === next) {
      if (had) current[id] = previous;
      else delete current[id];
      cache.saveToLocalStorage();
      cache.notifyListeners();
    }
    throw error;
  }
}
